const toIntOrNull = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isValuePresent = (value) => value !== null && value !== "";

// Normalizza un nome di comune per il confronto: lowercase, rimuove diacritici (NFD)
// e apostrofo finale usato come sostituto dell'accento (es. "CANTU'" → "cantu", "Cantù" → "cantu")
export const normalizeForComparison = (str) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/'$/, "");

export const getMappedImportEntry = ({ mappedValuesList, type, mappedValue }) => {
  const normalizedValue = (mappedValue || "").trim();
  return mappedValuesList.find(
    (item) => item.type === type && (item.mappedValue || "").trim() === normalizedValue
  );
};

export const resolveMappedIntValue = ({
  mappedValuesList,
  type,
  mappedValue,
  parsedSourceValues,
  fallback,
}) => {
  const normalizedValue = (mappedValue || "").trim();
  if (isValuePresent(normalizedValue) && parsedSourceValues.some(v => (v || "").trim() === normalizedValue)) {
    const mappedEntry = getMappedImportEntry({
      mappedValuesList,
      type,
      mappedValue: normalizedValue,
    });
    if (mappedEntry?.value !== undefined && mappedEntry?.value !== null) {
      return toIntOrNull(mappedEntry.value);
    }
  }

  return fallback();
};

export const getBaseIdByKey = (list, key) => {
  const entry = list.find((item) => item?.key === key);
  return toIntOrNull(entry?.id);
};

export const resolveIdentDocTipoFallback = ({
  baseTipoIdentList,
  baseTipoDocList,
  identTipo,
  isPg,
}) => {
  const tipoIdentItem = baseTipoIdentList.find((item) => item.id === identTipo);

  if (tipoIdentItem?.key === "tipo_identificazione_5") {
    return getBaseIdByKey(baseTipoDocList, "tipo_documento_8");
  }

  return isPg
    ? getBaseIdByKey(baseTipoDocList, "tipo_documento_3")
    : getBaseIdByKey(baseTipoDocList, "tipo_documento_1");
};

export const resolveMappedProvinciaComune = ({
  provincia,
  comune,
  parsedArrProvinceList,
  parsedArrComuniList,
  mappedValuesList,
  baseProvinceList,
  baseComuniList,
}) => {
  let mappedProvincia = provincia;
  let mappedComune = comune;

  if (mappedProvincia !== "EE") {
    mappedProvincia = resolveMappedIntValue({
      mappedValuesList,
      type: "provincia",
      mappedValue: mappedProvincia,
      parsedSourceValues: parsedArrProvinceList,
      fallback: () => {
        if (!isValuePresent(mappedProvincia)) {
          return null;
        }

        const provinciaEntry = baseProvinceList.find(
          (item) =>
            item?.sigla?.toLowerCase() === (mappedProvincia || "").toLowerCase()
        );
        return toIntOrNull(provinciaEntry?.id);
      },
    });

    mappedComune = resolveMappedIntValue({
      mappedValuesList,
      type: "comune",
      mappedValue: mappedComune,
      parsedSourceValues: parsedArrComuniList,
      fallback: () => {
        if (!isValuePresent(mappedComune)) {
          return null;
        }

        const normalizedInput = normalizeForComparison(mappedComune);
        const comuneEntry = baseComuniList.find(
          (item) => normalizeForComparison(item?.nome) === normalizedInput
        );
        return toIntOrNull(comuneEntry?.id);
      },
    });
  }

  return {
    provincia: mappedProvincia,
    comune: mappedComune,
  };
};

export const resolveMappedProfessionistaUserId = ({
  identCfProf,
  parsedArrProfList,
  mappedValuesList,
  utentiList,
  loggedUserId,
}) => {
  const normalizedCf = (identCfProf || "").trim();
  if (
    normalizedCf !== "" &&
    parsedArrProfList.some(v => (v || "").trim() === normalizedCf)
  ) {
    const mappedEntry = getMappedImportEntry({
      mappedValuesList,
      type: "professionista",
      mappedValue: normalizedCf,
    });

    if (mappedEntry?.value) {
      return mappedEntry.value;
    }
  }

  return utentiList.find((item) => item.cf === identCfProf)?.userId ?? loggedUserId;
};

export const parseItalianDateToUtc = (dateValue, utcHour = 12) => {
  if (!dateValue || dateValue === "") {
    return null;
  }

  const [day, month, year] = dateValue.split("/");
  const parsedDate = new Date(+year, +month - 1, +day);
  return new Date(
    Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      utcHour,
      0,
      0
    )
  );
};

export const buildImportDescription = ({ parsedFile, parsedValuesLength }) => {
  return (
    "Data: " +
    new Date().toString() +
    " - File: " +
    parsedFile +
    " - N. records file: " +
    parsedValuesLength.toString()
  );
};

export const splitNomeCognomeFromDenominazione = ({ denominazione, cf, onError }) => {
  const parts = denominazione.trim().split(" ").filter((part) => part.length > 0);

  if (parts.length === 0) {
    return { nome: "", cognome: "" };
  }
  if (parts.length === 1) {
    return { cognome: parts[0], nome: "" };
  }
  if (parts.length === 2) {
    return { cognome: parts[0], nome: parts[1] };
  }

  if (cf && cf.length === 16) {
    try {
      const getConsonants = (value) =>
        value.replace(/[^BCDFGHJKLMNPQRSTVWXYZ]/gi, "").toUpperCase();

      let cognomeWords = [];
      let consonantsFound = 0;
      let index = 0;

      while (index < parts.length && consonantsFound < 3) {
        cognomeWords.push(parts[index]);
        consonantsFound = getConsonants(cognomeWords.join("")).length;
        index++;
      }

      if (consonantsFound >= 3 && index < parts.length) {
        return {
          cognome: cognomeWords.join(" "),
          nome: parts.slice(index).join(" "),
        };
      }
    } catch (e) {
      if (onError) {
        onError(e);
      }
    }
  }

  if (parts.length === 3) {
    return {
      cognome: parts.slice(0, 2).join(" "),
      nome: parts[2],
    };
  }

  return {
    cognome: parts.slice(0, -2).join(" "),
    nome: parts.slice(-2).join(" "),
  };
};

export const resolvePaeseIdFromProvincia = async ({
  provincia,
  paeseEstero,
  fetchPaeseByIdent,
  fetchPaeseByNome,
}) => {
  const paeseByIdResp = await fetchPaeseByIdent({
    identificativo: "ITA",
  });

  let paeseId =
    paeseByIdResp?.data?.data?.base_paese?.length > 0
      ? paeseByIdResp.data.data.base_paese[0].Id
      : 0;

  if (provincia === "EE") {
    const paeseResp = await fetchPaeseByNome({
      nome: (paeseEstero || "").toLowerCase(),
    });

    if (paeseResp?.data?.data?.base_paese?.length > 0) {
      paeseId = paeseResp.data.data.base_paese[0].Id;
    }
  }

  return paeseId;
};

export const resolveGenereIdByValue = async ({
  genderValue,
  tenantId,
  fetchBaseGenere,
}) => {
  const baseGenereListResponse = await fetchBaseGenere({
    tenantId,
  });

  if (baseGenereListResponse?.data?.data?.base_genere?.length > 0) {
    const genereItem = baseGenereListResponse.data.data.base_genere.find(
      (item) => item.Value === genderValue
    );
    return genereItem ? genereItem.Id : 0;
  }

  return 0;
};
